/** XeAgents.ai**/
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "./ThemeProvider";

interface BrandingData {
  app_name: string;
  app_tagline: string;
  logo_url: string | null;
  logo_url_light: string | null;
  logo_url_dark: string | null;
  favicon_url: string | null;
  social_twitter_url: string | null;
  social_linkedin_url: string | null;
  social_github_url: string | null;
}

type BrandingProviderProps = {
  children: React.ReactNode;
};

type BrandingProviderState = {
  branding: BrandingData;
  currentLogo: string | null;
  isLoading: boolean;
  refetch: () => void;
};

const defaultBranding: BrandingData = {
  app_name: "VoicePro",
  app_tagline: "AI Voice Calling Agents & Lead Intelligence SaaS Platform",
  logo_url: null,
  logo_url_light: null,
  logo_url_dark: null,
  favicon_url: null,
  social_twitter_url: null,
  social_linkedin_url: null,
  social_github_url: null
};

const BrandingProviderContext = createContext<BrandingProviderState | undefined>(undefined);

export function BrandingProvider({ children }: BrandingProviderProps) {
  const [branding, setBranding] = useState<BrandingData>(defaultBranding);
  const { theme } = useTheme();

  const { data, isLoading, refetch } = useQuery<BrandingData>({
    queryKey: ["/api/branding"],
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) {
      setBranding({
        app_name: data.app_name || defaultBranding.app_name,
        app_tagline: data.app_tagline || defaultBranding.app_tagline,
        logo_url: data.logo_url,
        logo_url_light: data.logo_url_light,
        logo_url_dark: data.logo_url_dark,
        favicon_url: data.favicon_url,
        social_twitter_url: data.social_twitter_url,
        social_linkedin_url: data.social_linkedin_url,
        social_github_url: data.social_github_url
      });
    }
  }, [data]);

  useEffect(() => {
    if (branding.favicon_url) {
      const existingFavicon = document.querySelector("link[rel='icon']");
      if (existingFavicon) {
        existingFavicon.setAttribute("href", branding.favicon_url);
      } else {
        const favicon = document.createElement("link");
        favicon.rel = "icon";
        favicon.href = branding.favicon_url;
        document.head.appendChild(favicon);
      }
    }
  }, [branding.favicon_url]);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const currentLogo = theme === 'dark' 
    ? (branding.logo_url_dark || branding.logo_url_light || branding.logo_url)
    : (branding.logo_url_light || branding.logo_url);

  return (
    <BrandingProviderContext.Provider value={{ branding, currentLogo, isLoading, refetch: handleRefetch }}>
      {children}
    </BrandingProviderContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingProviderContext);
  if (context === undefined) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
}
